import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Volume2, Download, Loader2, Pause, CalendarHeart } from "lucide-react";
import { Panel, ScoreRing, Spectrum, NoteWheel } from "@/components/viz/ReportViz";

const STATUS_VAL = { strong: 100, normal: 72, weak: 35, missing: 0 };

const STATUS_BADGE = {
  strong: "bg-primary/15 text-primary border border-primary/30",
  normal: "bg-accent/20 text-foreground border border-accent/40",
  weak: "bg-secondary text-muted-foreground border border-border",
  missing: "bg-transparent text-muted-foreground/70 border border-dashed border-border",
};
const STATUS_LABEL = { strong: "Strong", normal: "Balanced", weak: "Weak", missing: "Missing" };

export default function ReportView({ report }) {
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const audioRef = useRef(null);

  const listen = async () => {
    if (audioRef.current && playing) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    if (audioRef.current) {
      audioRef.current.play();
      setPlaying(true);
      return;
    }
    setLoadingAudio(true);
    try {
      const res = await api.get(`/reports/${report.id}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audio.onended = () => setPlaying(false);
      audioRef.current = audio;
      audio.play();
      setPlaying(true);
    } catch {
      toast.error("Could not load audio narration.");
    } finally {
      setLoadingAudio(false);
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/reports/${report.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jvscan-${report.type}-report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid={`report-view-${report.type}`}>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-border pb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Health Report</p>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">{report.title}</h2>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <span className="block font-serif text-4xl text-primary" data-testid="report-score">{report.balance_score}</span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Balance / 100</span>
          </div>
        </div>
      </div>

      <p className="text-secondary-foreground text-base leading-relaxed max-w-2xl">{report.summary}</p>

      <div className="flex flex-wrap gap-3">
        <Button onClick={listen} disabled={loadingAudio} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-semibold" data-testid="report-listen-btn">
          {loadingAudio ? <Loader2 size={16} className="animate-spin" /> : playing ? <Pause size={16} strokeWidth={1.5} /> : <Volume2 size={16} strokeWidth={1.5} />}
          {playing ? "Pause narration" : "Listen to report"}
        </Button>
        <Button onClick={downloadPdf} disabled={pdfLoading} variant="outline" className="rounded-full border-border" data-testid="report-pdf-btn">
          {pdfLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} strokeWidth={1.5} />}
          Download PDF
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel title="Balance score" className="flex items-center justify-center">
          <ScoreRing value={report.balance_score} label="balance" />
        </Panel>
        <Panel title="Frequency spectrum" className="lg:col-span-2">
          <Spectrum data={report.findings.map((f) => ({ name: f.note, value: STATUS_VAL[f.status] ?? 0 }))} color="#22D3EE" />
        </Panel>
      </div>
      <Panel title="Note resonance wheel">
        <NoteWheel data={report.findings.map((f) => ({ note: f.note, value: STATUS_VAL[f.status] ?? 0 }))} color="#8B5CF6" />
      </Panel>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {report.findings.map((f) => (
          <div key={f.note} className="rounded-xl border border-border bg-card p-4 flex gap-3">
            <span className="grid place-items-center h-9 w-9 shrink-0 rounded-lg bg-secondary text-primary font-semibold text-sm">{f.note}</span>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold">{f.area}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_BADGE[f.status]}`}>{STATUS_LABEL[f.status]}</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{f.text}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <CalendarHeart className="text-primary shrink-0" strokeWidth={1.5} />
          <p className="text-sm text-secondary-foreground max-w-md">
            Want to understand these indicators with a practitioner? Book a one-to-one session to walk through your results.
          </p>
        </div>
        <Link to="/book"><Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-semibold whitespace-nowrap" data-testid="report-book-btn">Book a session</Button></Link>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed border-t border-border pt-6">{report.disclaimer}</p>
    </div>
  );
}
